import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import TutorialButton from './common/TutorialButton'; // Import TutorialButton
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/vistoriaimagem.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const VistoriaImagemManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_vistoria_imagem: '',
    id_imagem: '',
    id_vistoria: '',
    descr_manutencao_imagem: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [camposComErro, setCamposComErro] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'imagem da vistoria', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/vistoria-imagem/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'imagem da vistoria', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'imagem da vistoria');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'imagem da vistoria', { id });
    try {
      const response = await api.delete(`/vistoria-imagem/${id}`);
      logarSucesso('Excluir', 'imagem da vistoria', response.data);
      setDados(currentData => currentData.filter(item => item.id_vistoria_imagem !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'imagem da vistoria');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const getColunas = useMemo(() => {
    const colunasBase = [
      {
        title: 'ID Vistoria Imagem',
        field: 'id_vistoria_imagem',
        width: 200,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string'
      },
      {
        title: 'ID Imagem',
        field: 'id_imagem',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID Imagem...',
        sorter: 'string',
      },
      {
        title: 'ID Vistoria',
        field: 'id_vistoria',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID Vistoria...',
        sorter: 'string',
      },
      {
        title: 'Descrição Manutenção',
        field: 'descr_manutencao_imagem',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar descrição...',
        sorter: 'string',
      }
    ];

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_vistoria_imagem, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_vistoria_imagem}"?`)) {
            excluirRegistro(data.id_vistoria_imagem);
          }
        },
        headerSort: false
      },
      ...colunasBase.map(col => ({
        ...col,
        editor: col.field === 'id_vistoria_imagem' ? undefined : 'input'
      }))
    ];
  }, [atualizarRegistro, excluirRegistro]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: getColunas,
      layout: 'fitColumns',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_vistoria_imagem'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, getColunas]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/vistoria-imagem');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/vistoria-imagem');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      if (error.response && error.response.status === 403) {
        mostrarMensagem('error', 'Você não tem permissão para acessar este recurso.');
      } else {
        mostrarMensagem('error', `Erro de conexão: ${error.message}`);
      }
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
  }, [verificarAPI]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(getColunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator, getColunas]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { id_vistoria_imagem, id_imagem, id_vistoria, descr_manutencao_imagem } = formData;

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!id_vistoria_imagem) {
      camposObrigatorios.push('ID Vistoria Imagem');
      erros.push('id_vistoria_imagem');
    }
    if (!id_imagem) {
      camposObrigatorios.push('ID Imagem');
      erros.push('id_imagem');
    }
    if (!id_vistoria) {
      camposObrigatorios.push('ID Vistoria');
      erros.push('id_vistoria');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    const dataToSend = {
      id_vistoria_imagem,
      id_imagem,
      id_vistoria,
      descr_manutencao_imagem: descr_manutencao_imagem === '' ? null : descr_manutencao_imagem
    };

    logarRequisicao('Adicionar', 'imagem da vistoria', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/vistoria-imagem', dataToSend);
      logarSucesso('Adicionar', 'imagem da vistoria', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_vistoria_imagem: '', id_imagem: '', id_vistoria: '', descr_manutencao_imagem: '' });
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'imagem da vistoria');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Remove o erro do campo quando ele for preenchido
    if (value) {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'vistoria_imagem.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Vistoria Imagem</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_vistoria_imagem</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/vistoria imagem.mov" buttonText="Ver Tutorial Vistoria Imagem" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>
            {mensagem.texto}
          </div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_vistoria_imagem">ID Vistoria imagem:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="id_vistoria_imagem" name="id_vistoria_imagem" value={formData.id_vistoria_imagem} onChange={handleInputChange} placeholder="Ex: VI_001" maxLength={255} disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('id_vistoria_imagem') ? 'input-error' : ''} />
          </div>
          <div className="form-group">
            <label htmlFor="id_imagem">ID Imagem:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="id_imagem" name="id_imagem" value={formData.id_imagem} onChange={handleInputChange} placeholder="Ex: IMG_001" maxLength={255} disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('id_imagem') ? 'input-error' : ''} />
          </div>
          <div className="form-group">
            <label htmlFor="id_vistoria">ID Vistoria:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="id_vistoria" name="id_vistoria" value={formData.id_vistoria} onChange={handleInputChange} placeholder="Ex: VIST_001" maxLength={255} disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('id_vistoria') ? 'input-error' : ''} />
          </div>
          <div className="form-group">
            <label htmlFor="descr_manutencao_imagem">Descrição manutenção:</label>
            <input type="text" id="descr_manutencao_imagem" name="descr_manutencao_imagem" value={formData.descr_manutencao_imagem} onChange={handleInputChange} placeholder="Ex: Manutenção preventiva" maxLength={255} disabled={loading || apiStatus !== 'connected'} />
          </div>
          {/* Linha única: Adicionar, Recarregar, Exportar CSV + aviso de obrigatórios */}
          <div
            className="actions-row"
            style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'nowrap', marginTop: '10px' }}
          >
            <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
            </button>
            <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
            </button>
            <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>

            <div className="info-campos-obrigatorios" style={{ whiteSpace: 'nowrap' }}>
              <i className="fas fa-info-circle"></i>
              <span style={{ marginLeft: 6 }}>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default VistoriaImagemManager;
